import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { X, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react';
import { getCsrfToken } from '../lib/api';

interface DryRunResult {
  parser: { id: string; label: string };
  kind: string;
  meta: { reportName: string; clientName: string; accountName: string; accountNumber: string };
  lots: number;
  disposals: number;
  warnings: string[];
  resolution: {
    clientOutcome: string;
    clientId: string | null;
    clientLabel: string | null;
    accountExists: boolean;
  };
}

const OUTCOME_LABEL: Record<string, string> = {
  name_match: 'Matches existing client',
  will_create: 'A new client will be created',
  ambiguous: 'Multiple clients match — the import will need an explicit client',
  no_name: 'Report has no parseable client name',
  explicit: 'Using the selected client',
};

async function postImport(f: File, dryRun: boolean): Promise<Response> {
  const fd = new FormData();
  fd.append('file', f);
  if (dryRun) fd.append('dryRun', '1');
  // FormData request — skip the JSON api wrapper. The CSRF token lives in
  // memory (set from the login//me response); there is no csrf_token cookie.
  return fetch('/api/imports', {
    method: 'POST',
    body: fd,
    headers: { 'x-csrf-token': getCsrfToken() || '' },
  });
}

export function MacquarieUploadDialog({
  open,
  onClose,
  context
}: {
  open: boolean;
  onClose: () => void;
  context?: 'shell' | 'portfolio'
}) {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<DryRunResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  const reset = () => { setFile(null); setPreview(null); setError(''); };

  const dryRun = useMutation({
    mutationFn: async (f: File) => {
      const res = await postImport(f, true);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Could not read the report');
      }
      const body = await res.json();
      return body.data as DryRunResult;
    },
    onSuccess: (data) => { setPreview(data); setError(''); },
    onError: (e: Error) => setError(e.message || 'Could not read the report.'),
  });

  const upload = useMutation({
    mutationFn: async (f: File) => {
      const res = await postImport(f, false);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Upload failed');
      }
      return res.json();
    },
    onSuccess: (data) => {
      qc.invalidateQueries();
      reset();
      onClose();
      if (data.clientId && context === 'shell') {
        setLocation(`/clients/${data.clientId}`);
      }
    },
    onError: (e: Error) => setError(e.message || 'An error occurred during upload.'),
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-0">
      <div className="absolute inset-0 bg-ink/30 backdrop-blur-sm" onClick={() => { reset(); onClose(); }} />
      <div className="relative bg-paper rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between px-6 py-4 qa-hairline-b">
          <h2 className="text-lg font-medium text-ink flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-forest" />
            Upload Macquarie Report
          </h2>
          <button onClick={() => { reset(); onClose(); }} className="text-ink-mute hover:text-ink transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {!preview ? (
            <>
              <p className="text-sm text-ink-mute mb-4">
                Upload a Macquarie <b>Unrealised Gains/Losses</b> or <b>Realised Gains/Losses</b> Excel report (.xlsx).
                You'll see a preview of what will change before anything is written.
              </p>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${file ? 'border-forest bg-forest/5' : 'border-paper-trim hover:border-forest/50'}`}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".xlsx"
                  onChange={(e) => {
                    if (e.target.files?.[0]) { setFile(e.target.files[0]); setError(''); }
                  }}
                />
                {file ? (
                  <div>
                    <p className="text-sm font-medium text-ink">{file.name}</p>
                    <p className="text-xs text-ink-mute mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                ) : (
                  <div className="text-ink-mute flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 opacity-50" />
                    <p className="text-sm">Click to select an .xlsx file</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-sm space-y-3">
              <p className="font-medium text-ink flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-forest" /> {preview.parser.label}
              </p>
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-ink-soft">
                <dt className="text-ink-mute">Report</dt><dd>{preview.meta.reportName}</dd>
                <dt className="text-ink-mute">Client</dt>
                <dd>
                  {preview.meta.clientName}
                  <span className="block text-xs text-ink-mute">
                    {OUTCOME_LABEL[preview.resolution.clientOutcome] ?? preview.resolution.clientOutcome}
                    {preview.resolution.clientLabel ? ` (${preview.resolution.clientLabel})` : ''}
                  </span>
                </dd>
                <dt className="text-ink-mute">Account</dt>
                <dd>{preview.meta.accountNumber}{preview.resolution.accountExists ? ' (existing)' : ' (will be created)'}</dd>
                <dt className="text-ink-mute">Parcels</dt>
                <dd>{preview.lots} lot{preview.lots === 1 ? '' : 's'}{preview.disposals ? ` · ${preview.disposals} disposal${preview.disposals === 1 ? '' : 's'}` : ''}</dd>
              </dl>
              {preview.warnings.length > 0 && (
                <div className="text-xs text-rust space-y-1">
                  {preview.warnings.map((w, i) => (
                    <p key={i} className="flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{w}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && <p className="text-sm text-rust mt-4">{error}</p>}
        </div>

        <div className="px-6 py-4 bg-paper-subtle qa-hairline-t flex justify-end gap-3">
          {!preview ? (
            <>
              <button onClick={() => { reset(); onClose(); }} className="qa-btn-secondary">Cancel</button>
              <button
                className="qa-btn-primary flex items-center gap-2"
                disabled={!file || dryRun.isPending}
                onClick={() => { if (file) dryRun.mutate(file); }}
              >
                {dryRun.isPending ? 'Reading…' : 'Preview'}
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setPreview(null)} className="qa-btn-secondary">Back</button>
              <button
                className="qa-btn-primary flex items-center gap-2"
                disabled={!file || upload.isPending || preview.resolution.clientOutcome === 'ambiguous'}
                onClick={() => { if (file) upload.mutate(file); }}
              >
                {upload.isPending ? 'Importing…' : 'Confirm import'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
